import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type { AlignItemsProps, BorderRadiusProps, MinWidthProps } from 'styled-system';
import { alignItems, borderRadius, minWidth, space, themeGet } from 'styled-system';

import { useLocalization } from '../../providers/LocaleProvider';
import type { SpaceProps } from '../../theme/system-types';
import { Button } from '../Button';
import { BodyRegularBR2 } from '../Typography';

interface ButtonVariantProps {
  code: string;
  onClick: () => void;
  codeText: React.ReactNode;
  container: React.ComponentType<{
    onClick: () => void;
    children: React.ReactNode;
  }>;
}

const StyledButton = styled(Button)<
  SpaceProps & BorderRadiusProps & AlignItemsProps & MinWidthProps
>`
  ${space};
  ${borderRadius};
  ${alignItems};
  ${minWidth};

  &:focus-visible {
    outline-offset: 0;
    outline-width: 1px;
    border-color: ${themeGet('colors.sys.secondary.border.focus')};
  }
`;

const buttonStyles: ComponentProps<typeof StyledButton> = {
  kind: 'secondary',
  py: 'sp_6',
  ml: ['sp_8', 'sp_16'],
  borderRadius: 'x-large',
  alignItems: 'center',
};

export const CodeWithButton = ({
  code,
  codeText,
  container: Container,
  onClick,
}: ButtonVariantProps) => {
  const { t, r } = useLocalization();

  return (
    <Container onClick={onClick}>
      {codeText}
      <StyledButton aria-label={r.t('h24_redeem_voucher', { voucherCode: code })} {...buttonStyles}>
        <BodyRegularBR2 as="span" fontWeight="normal" color="inherit">
          {t('h24_voucher_button')}
        </BodyRegularBR2>
      </StyledButton>
    </Container>
  );
};
